import React, { useState } from 'react';
import { supabase } from '@/lib/safeSupabase';
import { useAuth } from '@/hooks/useAuth';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Plus, Trash2, ArrowRightLeft, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { sanitizeError } from '@/lib/errorMessages';
import { createOrgLogger } from '@/lib/audit';
import { formatDistanceToNow } from 'date-fns';

export default function CaseAssignments() {
  const { user, org } = useAuth();
  const queryClient = useQueryClient();
  const [assignOpen, setAssignOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedAgent, setSelectedAgent] = useState('');
  const [reassignTarget, setReassignTarget] = useState<{ id: string; clientId: string } | null>(null);
  const [reassignAgent, setReassignAgent] = useState('');

  const logAudit = createOrgLogger(org?.id);

  const { data: assignments = [], isLoading } = useQuery({
    queryKey: ['org-assignments', org?.id],
    queryFn: async () => {
      if (!org) return [];
      const { data, error } = await supabase.from('case_assignments').select('*').eq('org_id', org.id).order('created_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!org,
  });

  const { data: clients = [] } = useQuery({
    queryKey: ['org-client-ids', org?.id],
    queryFn: async () => {
      if (!org) return [];
      const { data, error } = await supabase.from('scenarios').select('client_id, name').eq('org_id', org.id).order('created_at', { ascending: true });
      if (error) throw error;
      const map = new Map<string, string>();
      for (const r of data ?? []) { if (!map.has(r.client_id)) map.set(r.client_id, r.name); }
      return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
    },
    enabled: !!org,
  });

  const { data: members = [] } = useQuery({
    queryKey: ['org-members-list', org?.id],
    queryFn: async () => {
      if (!org) return [];
      const { data, error } = await supabase.from('profiles').select('id, full_name, email').eq('org_id', org.id).neq('status', 'deactivated');
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!org,
  });

  const memberMap = React.useMemo(() => {
    const m = new Map<string, { full_name: string | null; email: string | null }>();
    for (const p of members) m.set(p.id, p);
    return m;
  }, [members]);

  const clientNameMap = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const c of clients) m.set(c.id, c.name);
    return m;
  }, [clients]);

  const assignedClientIds = React.useMemo(() => new Set(assignments.map(a => a.client_id)), [assignments]);
  const availableClients = clients.filter(c => !assignedClientIds.has(c.id));

  const assignMut = useMutation({
    mutationFn: async () => {
      if (!selectedClient || !selectedAgent || !org) throw new Error('Select both');
      const { error } = await supabase.from('case_assignments').insert({
        client_id: selectedClient, assigned_to: selectedAgent, assigned_by: user!.id, org_id: org.id,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-assignments'] });
      logAudit('assignment.created', 'assignment', undefined, { client: clientNameMap.get(selectedClient), agent: memberMap.get(selectedAgent)?.full_name || memberMap.get(selectedAgent)?.email });
      setAssignOpen(false); setSelectedClient(''); setSelectedAgent('');
      toast.success('Client assigned');
    },
    onError: (err: any) => toast.error('Error', { description: sanitizeError(err) }),
  });

  const unassignMut = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('case_assignments').delete().eq('id', id);
      if (error) throw error;
      return id;
    },
    onSuccess: (id) => {
      queryClient.invalidateQueries({ queryKey: ['org-assignments'] });
      logAudit('assignment.removed', 'assignment', id);
      toast.success('Assignment removed');
    },
    onError: (err: any) => toast.error('Error', { description: sanitizeError(err) }),
  });

  const reassignMut = useMutation({
    mutationFn: async () => {
      if (!reassignTarget || !reassignAgent || !org) throw new Error('Missing data');
      // Delete old, insert new
      await supabase.from('case_assignments').delete().eq('id', reassignTarget.id);
      const { error } = await supabase.from('case_assignments').insert({
        client_id: reassignTarget.clientId, assigned_to: reassignAgent, assigned_by: user!.id, org_id: org.id,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-assignments'] });
      logAudit('assignment.reassigned', 'assignment', undefined, { client_id: reassignTarget?.clientId, new_agent: reassignAgent });
      setReassignTarget(null); setReassignAgent('');
      toast.success('Client reassigned');
    },
    onError: (err: any) => toast.error('Error', { description: sanitizeError(err) }),
  });

  if (!org) {
    return <Card><CardContent className="py-12 text-center text-muted-foreground">No account found.</CardContent></Card>;
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-lg">Client Assignments ({assignments.length})</CardTitle>
            <CardDescription>Assign clients to team members</CardDescription>
          </div>
          <Button size="sm" onClick={() => setAssignOpen(true)}>
            <Plus className="h-3.5 w-3.5 mr-1" /> Assign Client
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : assignments.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <ArrowRightLeft className="h-10 w-10 mx-auto mb-3 opacity-20" />
              <p className="text-sm">No assignments yet. Assign clients to agents to get started.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Assigned To</TableHead>
                  <TableHead>Assigned By</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="w-28">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {assignments.map(a => {
                  const assignee = memberMap.get(a.assigned_to);
                  const assigner = memberMap.get(a.assigned_by);
                  return (
                    <TableRow key={a.id}>
                      <TableCell className="font-medium text-sm">{clientNameMap.get(a.client_id) || a.client_id.slice(0, 8) + '…'}</TableCell>
                      <TableCell className="text-sm">{assignee?.full_name || assignee?.email || 'Unknown'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{assigner?.full_name || assigner?.email || 'Unknown'}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{formatDistanceToNow(new Date(a.created_at), { addSuffix: true })}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setReassignTarget({ id: a.id, clientId: a.client_id }); setReassignAgent(''); }}>
                            <RefreshCw className="h-3 w-3 mr-1" /> Reassign
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive" onClick={() => unassignMut.mutate(a.id)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Assign Dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Assign Client</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Client</Label>
              <Select value={selectedClient} onValueChange={setSelectedClient}>
                <SelectTrigger><SelectValue placeholder="Select a client" /></SelectTrigger>
                <SelectContent>
                  {availableClients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Assign To</Label>
              <Select value={selectedAgent} onValueChange={setSelectedAgent}>
                <SelectTrigger><SelectValue placeholder="Select a team member" /></SelectTrigger>
                <SelectContent>
                  {members.map(m => <SelectItem key={m.id} value={m.id}>{m.full_name || m.email || 'Unknown'}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(false)}>Cancel</Button>
            <Button onClick={() => assignMut.mutate()} disabled={!selectedClient || !selectedAgent || assignMut.isPending}>Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reassign Dialog */}
      <Dialog open={!!reassignTarget} onOpenChange={open => { if (!open) setReassignTarget(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Reassign Client</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>New Agent</Label>
              <Select value={reassignAgent} onValueChange={setReassignAgent}>
                <SelectTrigger><SelectValue placeholder="Select a team member" /></SelectTrigger>
                <SelectContent>
                  {members.map(m => <SelectItem key={m.id} value={m.id}>{m.full_name || m.email || 'Unknown'}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassignTarget(null)}>Cancel</Button>
            <Button onClick={() => reassignMut.mutate()} disabled={!reassignAgent || reassignMut.isPending}>Reassign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
